import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Boxes, Package, Percent, ShieldCheck } from 'lucide-react';
import { TaxCategorySelect } from '@/components/TaxCategorySelect';
import { get, post, put } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { RecordState } from '@/components/ui/record-state';
import { Alert, Checkbox, Field, Input } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { safeReturnTo } from '../opd/BookAppointmentPage';
import type { ProductRow } from './InventoryPage';

const PRODUCT_LOOKUP_LIMIT = 200;
const PRODUCTS_TAB = '/inventory?tab=products';

function initialForm(product: ProductRow | null) {
  return {
    type: product?.type ?? 'drug',
    code: product?.code ?? '',
    genericName: product?.genericName ?? '',
    brandName: product?.brandName ?? '',
    form: product?.form ?? '',
    strength: product?.strength ?? '',
    unit: product?.unit ?? 'unit',
    packSize: String(product?.packSize ?? 1),
    manufacturer: product?.manufacturer ?? '',
    taxPct: String(product?.taxPct ?? 0),
    taxCategory: product?.taxCategory ?? '',
    reorderLevel: String(product?.reorderLevel ?? 0),
    isRxRequired: product?.isRxRequired ?? false,
    isControlled: product?.isControlled ?? false,
  };
}

export function ProductPage() {
  const { productId } = useParams<{ productId: string }>();
  const [params] = useSearchParams();
  const location = useLocation();
  const returnTo = safeReturnTo(params.get('returnTo'), PRODUCTS_TAB);
  const carried = (location.state as { product?: ProductRow } | null)?.product;
  const fromState = carried && carried.id === productId ? carried : null;

  const lookup = useQuery({
    queryKey: ['products'],
    queryFn: () => get<ProductRow[]>(`/pharmacy/products?limit=${PRODUCT_LOOKUP_LIMIT}`),
    enabled: Boolean(productId) && fromState === null,
  });

  if (!productId) return <ProductForm key="new" product={null} returnTo={returnTo} />;
  const product = fromState ?? lookup.data?.find((row) => row.id === productId) ?? null;
  if (product) return <ProductForm key={product.id} product={product} returnTo={returnTo} />;
  return <RecordState error={lookup.error} missing={lookup.isSuccess} />;
}

function ProductForm({ product, returnTo }: { product: ProductRow | null; returnTo: string }) {
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [initial] = useState(() => initialForm(product));
  const [form, setForm] = useState(initial);
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => {
      const body = {
        type: form.type,
        code: form.code,
        genericName: form.genericName,
        brandName: form.brandName || undefined,
        form: form.form || undefined,
        strength: form.strength || undefined,
        unit: form.unit || 'unit',
        packSize: Number(form.packSize) || 1,
        manufacturer: form.manufacturer || undefined,
        taxPct: Number(form.taxPct) || 0,
        taxCategory: form.taxCategory || null,
        reorderLevel: Number(form.reorderLevel) || 0,
        isRxRequired: form.isRxRequired,
        isControlled: form.isControlled,
      };
      return product ? put(`/pharmacy/products/${product.id}`, body) : post('/pharmacy/products', body);
    },
    onSuccess: () => {
      toast.success(product ? tr('pharmacy', 'product_updated') : tr('pharmacy', 'product_added'), product ? undefined : tr('pharmacy', 'receive_stock_against_it_next'));
      void queryClient.invalidateQueries({ queryKey: ['products'] });
      navigate(returnTo, { replace: true });
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const missing: string[] = [];
  if (form.code.trim().length < 2) missing.push('a code');
  if (form.genericName.trim().length < 2) missing.push('a generic name');

  const dirty = !save.isSuccess && JSON.stringify(form) !== JSON.stringify(initial);

  return (
    <FormScreen
      title={product ? tr('pharmacy', 'edit_x', { value: product.brandName ?? product.genericName }) : tr('pharmacy', 'add_a_product')}
      description={tr('pharmacy', 'what_it_is_what_it_costs_comes_later_with_the')}
      eyebrow={tr('pharmacy', 'inventory')}
      icon={Package}
      backTo={returnTo}
      dirty={dirty}
      onSubmit={(event) => {
        event.preventDefault();
        if (missing.length > 0) return;
        setError(null);
        save.mutate();
      }}
    >
      <FormSection title={tr('pharmacy', 'product')} icon={Package}>
        <Field label={tr('pharmacy', 'code')} required hint={tr('pharmacy', 'your_own_short_code_it_appears_on_the_shelf')}>
          <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} />
        </Field>
        <Field label={tr('pharmacy', 'type')}>
          <ChoiceButtons
            layout="chips"
            value={form.type}
            onChange={(type) => setForm({ ...form, type })}
            options={[
              { value: 'drug', label: tr('pharmacy', 'drug') },
              { value: 'consumable', label: tr('pharmacy', 'consumable') },
              { value: 'asset', label: tr('pharmacy', 'asset') },
              { value: 'service_item', label: tr('pharmacy', 'service_item') },
            ]}
          />
        </Field>
        <Field label={tr('pharmacy', 'generic_name')} required>
          <Input value={form.genericName} onChange={(e) => setForm({ ...form, genericName: e.target.value })} />
        </Field>
        <Field label={tr('pharmacy', 'brand_name')} hint={tr('pharmacy', 'what_the_customer_asks_for')}>
          <Input value={form.brandName} onChange={(e) => setForm({ ...form, brandName: e.target.value })} />
        </Field>
        <Field label={tr('pharmacy', 'form')} hint={tr('pharmacy', 'tablet_capsule_syrup_injection')}>
          <Input value={form.form} onChange={(e) => setForm({ ...form, form: e.target.value })} />
        </Field>
        <Field label={tr('pharmacy', 'strength')} hint={tr('pharmacy', '500_mg_5_ml')}>
          <Input value={form.strength} onChange={(e) => setForm({ ...form, strength: e.target.value })} />
        </Field>
        <Field label={tr('pharmacy', 'manufacturer')} className="sm:col-span-2">
          <Input value={form.manufacturer} onChange={(e) => setForm({ ...form, manufacturer: e.target.value })} />
        </Field>
      </FormSection>

      <FormSection title={tr('pharmacy', 'stock')} icon={Boxes} columns={3}>
        <Field label={tr('pharmacy', 'sold_as')} hint={tr('pharmacy', 'tablet_bottle_piece')}>
          <Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} />
        </Field>
        <Field label={tr('pharmacy', 'pack_size')} hint={tr('pharmacy', 'how_many_units_in_one_pack')}>
          <Input type="number" min={1} value={form.packSize} onChange={(e) => setForm({ ...form, packSize: e.target.value })} />
        </Field>
        <Field label={tr('pharmacy', 'reorder_at')} hint={tr('pharmacy', 'warn_when_the_shelf_falls_to_this')}>
          <Input type="number" min={0} value={form.reorderLevel} onChange={(e) => setForm({ ...form, reorderLevel: e.target.value })} />
        </Field>
      </FormSection>

      <FormSection title={tr('billing', 'pricing')} icon={Percent}>
        <Field label={tr('pharmacy', 'tax')} hint={tr('pharmacy', 'applied_when_it_is_sold')}>
          <Input type="number" min={0} max={100} value={form.taxPct} onChange={(e) => setForm({ ...form, taxPct: e.target.value })} />
        </Field>
        <TaxCategorySelect value={form.taxCategory} onChange={(taxCategory) => setForm({ ...form, taxCategory })} />
      </FormSection>

      <FormSection title={tr('pharmacy', 'flags')} icon={ShieldCheck} columns={1}>
        <Checkbox
          checked={form.isRxRequired}
          onCheckedChange={(checked) => setForm({ ...form, isRxRequired: checked })}
          label={tr('pharmacy', 'needs_a_prescription')}
        />
        <Checkbox
          checked={form.isControlled}
          onCheckedChange={(checked) => setForm({ ...form, isControlled: checked })}
          label={tr('pharmacy', 'controlled_drug_kept_in_the_register')}
        />
      </FormSection>

      {error && <Alert variant="destructive">{error}</Alert>}

      <FormActions
        submitLabel={product ? tr('pharmacy', 'save_changes') : tr('pharmacy', 'add_product')}
        loading={save.isPending}
        incomplete={missing.length > 0}
        cancelTo={returnTo}
        note={missing.length > 0 ? `${tr('pharmacy', 'still_needed')} ${missing.join(', ')}` : undefined}
      />
    </FormScreen>
  );
}
